import React, { useState } from "react";
import { X, Plus, Trash2, GripVertical, Image as ImageIcon, Upload } from "lucide-react";
import { api } from "../../lib/api";

const QTYPES = [
  { value: "mcq", label: "MCQ" },
  { value: "truefalse", label: "True / False" },
  { value: "short", label: "Short answer" },
];

/** Teacher quiz builder — add any number of MCQ / T-F / short questions,
 * each with an optional reference image uploaded to /api/files. */
export default function QuizBuilderModal({ groupId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [questions, setQuestions] = useState([newQuestion("mcq")]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const setQ = (idx, patch) => {
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };
  const addQ = (type = "mcq") => setQuestions((qs) => [...qs, newQuestion(type)]);
  const rmQ = (idx) => setQuestions((qs) => qs.filter((_, i) => i !== idx));

  const handleImage = async (idx, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "resource");
    try {
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQ(idx, { image_file_id: r.data.file_id, image_filename: r.data.filename });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Image upload failed");
    }
  };

  const handleSave = async () => {
    if (!title.trim()) return setErr("Please give the quiz a title");
    if (questions.length === 0) return setErr("Add at least one question");
    for (const [i, q] of questions.entries()) {
      if (!q.prompt.trim()) return setErr(`Q${i + 1}: prompt is required`);
      if (q.type === "mcq") {
        const opts = q.options.filter((o) => o.text.trim());
        if (opts.length < 2) return setErr(`Q${i + 1}: MCQ needs ≥2 options`);
        if (q.correct_index == null || q.correct_index >= opts.length) return setErr(`Q${i + 1}: pick a correct option`);
      }
      if (q.type === "truefalse" && q.correct_bool == null) return setErr(`Q${i + 1}: pick true or false`);
    }
    setErr("");
    setSaving(true);
    try {
      await api.post(`/staff/groups/${groupId}/assignments`, {
        type: "teacher_quiz",
        title: title.trim(),
        description: description.trim(),
        due_date: dueDate || null,
        questions: questions.map((q) => ({
          type: q.type,
          prompt: q.prompt.trim(),
          options: q.type === "mcq" ? q.options.map((o) => o.text.trim()).filter(Boolean) : [],
          correct_index: q.type === "mcq" ? q.correct_index : null,
          correct_bool: q.type === "truefalse" ? q.correct_bool : null,
          correct_text: q.type === "short" ? (q.correct_text || "") : null,
          explanation: q.explanation || "",
          image_file_id: q.image_file_id || null,
        })),
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to create quiz");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="quiz-builder-modal">
      <div className="bg-bone w-full max-w-4xl rounded-3xl border border-border overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-white">
          <div>
            <div className="font-display text-lg font-semibold">New quiz</div>
            <div className="text-xs text-muted-foreground">Build a custom quiz — MCQ, True/False, or short answer.</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-secondary" data-testid="quiz-builder-close">
            <X className="w-4 h-4 mx-auto" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Mid-term mock"
                data-testid="quiz-builder-title"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
                maxLength={200}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Due date (optional)</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="quiz-builder-due"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="quiz-builder-description"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage min-h-[50px]"
              maxLength={500}
            />
          </div>

          <div className="space-y-3">
            {questions.map((q, idx) => (
              <QuestionEditor
                key={q._key}
                index={idx}
                q={q}
                onChange={(patch) => setQ(idx, patch)}
                onRemove={() => rmQ(idx)}
                onImage={(file) => handleImage(idx, file)}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => addQ("mcq")} data-testid="quiz-builder-add-mcq"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sage/10 hover:bg-sage/20 text-sage text-sm font-medium"><Plus className="w-4 h-4" /> MCQ</button>
            <button onClick={() => addQ("truefalse")} data-testid="quiz-builder-add-tf"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-terracotta/10 hover:bg-terracotta/20 text-terracotta text-sm font-medium"><Plus className="w-4 h-4" /> True/False</button>
            <button onClick={() => addQ("short")} data-testid="quiz-builder-add-short"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 text-sm font-medium"><Plus className="w-4 h-4" /> Short answer</button>
          </div>

          {err && <div className="text-xs text-terracotta" data-testid="quiz-builder-error">{err}</div>}
        </div>

        <footer className="px-6 py-4 border-t border-border bg-white flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {questions.length} question{questions.length !== 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-sage" data-testid="quiz-builder-cancel">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="quiz-builder-save"
              className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark disabled:opacity-60 text-white font-medium px-5 py-2 rounded-lg transition-all"
            >
              {saving ? "Publishing…" : "Publish quiz"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function newQuestion(type) {
  return {
    _key: `q_${Math.random().toString(36).slice(2, 10)}`,
    type,
    prompt: "",
    options: type === "mcq" ? [newOption(), newOption(), newOption(), newOption()] : [],
    correct_index: type === "mcq" ? 0 : null,
    correct_bool: type === "truefalse" ? true : null,
    correct_text: "",
    explanation: "",
    image_file_id: null,
    image_filename: null,
  };
}

function newOption() {
  return { _key: `opt_${Math.random().toString(36).slice(2, 10)}`, text: "" };
}

function QuestionEditor({ index, q, onChange, onRemove, onImage }) {
  const typeBadgeClass =
    q.type === "mcq" ? "bg-sage/10 text-sage" :
    q.type === "truefalse" ? "bg-terracotta/10 text-terracotta" :
    "bg-amber-100 text-amber-700";

  return (
    <div className="bg-white border border-border rounded-2xl p-4 space-y-3" data-testid={`quiz-question-${index}`}>
      <div className="flex items-center gap-2">
        <GripVertical className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground">Q{index + 1}</span>
        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full ${typeBadgeClass}`}>
          {QTYPES.find((t) => t.value === q.type)?.label}
        </span>
        <div className="flex-1" />
        <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-sage cursor-pointer" data-testid={`quiz-q-${index}-image-label`}>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => onImage(e.target.files?.[0])} data-testid={`quiz-q-${index}-image-input`} />
          {q.image_file_id ? (
            <><ImageIcon className="w-3.5 h-3.5 text-sage" /> Image attached</>
          ) : (
            <><Upload className="w-3.5 h-3.5" /> Attach image</>
          )}
        </label>
        <button onClick={onRemove} className="w-7 h-7 rounded-lg hover:bg-terracotta/10 hover:text-terracotta text-muted-foreground" data-testid={`quiz-q-${index}-remove`}>
          <Trash2 className="w-3.5 h-3.5 mx-auto" />
        </button>
      </div>

      <textarea
        value={q.prompt}
        onChange={(e) => onChange({ prompt: e.target.value })}
        placeholder="Question prompt"
        data-testid={`quiz-q-${index}-prompt`}
        className="w-full px-3 py-2 rounded-lg border border-border bg-bone text-sm focus:outline-none focus:border-sage min-h-[44px]"
        maxLength={1000}
      />

      {q.type === "mcq" && (
        <div className="space-y-2">
          {q.options.map((opt, i) => (
            <div key={opt._key} className="flex items-center gap-2">
              <input
                type="radio"
                name={`q${index}-correct`}
                checked={q.correct_index === i}
                onChange={() => onChange({ correct_index: i })}
                data-testid={`quiz-q-${index}-correct-${i}`}
                className="w-4 h-4 accent-sage"
              />
              <input
                value={opt.text}
                onChange={(e) => onChange({
                  options: q.options.map((o, j) => (j === i ? { ...o, text: e.target.value } : o)),
                })}
                placeholder={`Option ${i + 1}`}
                data-testid={`quiz-q-${index}-opt-${i}`}
                className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-bone text-sm focus:outline-none focus:border-sage"
              />
              {q.options.length > 2 && (
                <button onClick={() => onChange({ options: q.options.filter((_, j) => j !== i) })}
                  className="w-7 h-7 rounded-lg hover:bg-terracotta/10 text-muted-foreground">
                  <Trash2 className="w-3.5 h-3.5 mx-auto" />
                </button>
              )}
            </div>
          ))}
          {q.options.length < 6 && (
            <button onClick={() => onChange({ options: [...q.options, newOption()] })} data-testid={`quiz-q-${index}-add-opt`}
              className="text-xs text-sage hover:text-sage-dark font-medium">+ Add option</button>
          )}
        </div>
      )}

      {q.type === "truefalse" && (
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="radio" name={`q${index}-tf`} checked={q.correct_bool === true} onChange={() => onChange({ correct_bool: true })} data-testid={`quiz-q-${index}-tf-true`} className="accent-sage" /> True
          </label>
          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="radio" name={`q${index}-tf`} checked={q.correct_bool === false} onChange={() => onChange({ correct_bool: false })} data-testid={`quiz-q-${index}-tf-false`} className="accent-sage" /> False
          </label>
        </div>
      )}

      {q.type === "short" && (
        <input
          value={q.correct_text}
          onChange={(e) => onChange({ correct_text: e.target.value })}
          placeholder="Reference answer (for your reference while grading — not shown to students)"
          data-testid={`quiz-q-${index}-ref`}
          className="w-full px-3 py-1.5 rounded-lg border border-border bg-bone text-sm focus:outline-none focus:border-sage"
        />
      )}

      <input
        value={q.explanation}
        onChange={(e) => onChange({ explanation: e.target.value })}
        placeholder="Explanation (optional, shown after submission)"
        data-testid={`quiz-q-${index}-explanation`}
        className="w-full px-3 py-1.5 rounded-lg border border-border bg-bone text-xs text-muted-foreground focus:outline-none focus:border-sage"
      />
    </div>
  );
}
